import Lottie from "lottie-react";
import { Screen, usePodContext } from "../../PodContext";

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import "./Meditation.css";

import loaderAnimation from "../../../assets/animations/loader.json";
import Text from "../Text";
import VolumeSlider from "../VolumeSlider";

import mixpanel from "mixpanel-browser";
import ExitModal from "./ExitModal";
import captionsMap from "../../../constants/captions";

const Meditation = () => {
  const { scene, audio } = usePodContext();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(true);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [currentCaption, setCurrentCaption] = useState<string | null>(null);

  const meditationAudio = useRef<HTMLAudioElement>(null);

  const handleTimeUpdate = useCallback(() => {
    if (!meditationAudio.current) return;
    const t = meditationAudio.current.currentTime;
    const lines = captionsMap[audio.id] ?? [];
    const match = lines.find((c) => t >= c.start && t < c.end);
    setCurrentCaption(match?.text ?? null);
  }, [audio.id]);

  useEffect(() => {
    if (meditationAudio.current) {
      meditationAudio.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      setIsLoading(false);
      setReady(true);
    }, 3000);

    return () => clearTimeout(loadingTimeout);
  }, []);

  useEffect(() => {
    if (ready && meditationAudio.current) {
      meditationAudio.current.play();
    }

    return () => {
      if (meditationAudio.current) {
        meditationAudio.current.pause();
        meditationAudio.current.currentTime = 0;
      }
    };
  }, [ready]);

  const audioEnded = useCallback(() => {
    setTimeout(() => {
      navigate(Screen.POST_FEELING);
    }, 2500);
  }, [navigate]);

  const handleConfirmExit = () => {
    if (meditationAudio.current) {
      meditationAudio.current.pause();
      meditationAudio.current.currentTime = 0;
    }

    mixpanel.track("Meditation Ended Early");
    navigate(Screen.POST_FEELING);
  };

  const toggleVolumeSlider = () => {
    setShowVolumeSlider(!showVolumeSlider);
  };

  return (
    <div className="meditation-container">
      <div className="meditation-scene-container">
        <video
          className="meditation-background-video"
          src={scene.video}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 1,
          }}
        />

        <audio
          ref={meditationAudio}
          src={audio.audioUrl}
          preload="auto"
          onEnded={audioEnded}
          onTimeUpdate={handleTimeUpdate}
        />

        {!isLoading && (
          <>
            <div className="speaker-icon-container">
              <button className="speaker-button" onClick={toggleVolumeSlider}>
                <img
                  src="/assets/icons/Volume-Icon.svg"
                  alt="Volume"
                  width={22}
                  height={18}
                />
              </button>
            </div>

            {showVolumeSlider && (
              <div className="volume-slider-container">
                <button
                  className="volume-button"
                  onClick={() => setVolume(Math.max(0, volume - 0.1))}
                >
                  <Text>-</Text>
                </button>

                <VolumeSlider
                  volume={volume}
                  onVolumeChange={setVolume}
                />

                <button
                  className="volume-button"
                  onClick={() => setVolume(Math.min(1, volume + 0.1))}
                >
                  <Text>+</Text>
                </button>
              </div>
            )}

            <div className="meditation-exit-button-container">
              {(captionsMap[audio.id]?.length ?? 0) > 0 && (
                <button
                  className={`cc-button${captionsEnabled ? " cc-button--active" : ""}`}
                  onClick={() => setCaptionsEnabled((v) => !v)}
                  aria-label="Toggle captions"
                >
                  CC
                </button>
              )}
              <button className="exit-button" onClick={() => setShowExitModal(true)}>
                <img
                  src="/assets/icons/Exit-Icon.svg"
                  alt="Close"
                  width={40}
                  height={40}
                />
              </button>
            </div>

            {captionsEnabled && currentCaption && (
              <div className="meditation-caption">
                <span>{currentCaption}</span>
              </div>
            )}
          </>
        )}
      </div>

      {isLoading && (
        <div className="meditation-loading-screen">
          <Lottie
            animationData={loaderAnimation}
            style={{ width: 200, height: 200 }}
          />
          <Text type="subtitle">Oasis loading...</Text>
        </div>
      )}

      <ExitModal
        isOpen={showExitModal}
        onConfirm={handleConfirmExit}
        onCancel={() => setShowExitModal(false)}
      />
    </div>
  );
};

export default Meditation;